import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ScheduleIcon from '@mui/icons-material/Schedule';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';

import { useGetDealCrawlersList } from '~/hooks/apis/cruises/useGetDealCrawlersList';

import { formatDateShort } from '~/services/TimeService';
import { CruiseDealCrawler } from '~/services/cruises/DealCrawlerService';

const MAX_SELECTION = 6;

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: (selected: Array<CruiseDealCrawler>, mainCrawlerId: string) => void;
  /** Optional vendor ID to filter crawlers (e.g. from the default crawler). When omitted, all crawlers are listed. */
  defaultVendorId?: string;
  /** Crawler IDs already included (e.g. current page) — shown in the list with checkboxes pre-selected. */
  selectedCrawlerIds?: Array<string>;
  /** Current main crawler ID (e.g. from parent page). Used to pre-select main when modal opens. */
  mainCrawlerId?: string;
}

export default function DealCrawlerPageCreationSelectionModal({
  open,
  onClose,
  onConfirm,
  defaultVendorId,
  selectedCrawlerIds = [],
  mainCrawlerId: mainCrawlerIdProp,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [nameFilter, setNameFilter] = useState<string>('');
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [mainCrawlerId, setMainCrawlerId] = useState<string | null>(null);

  const { data, isLoading } = useGetDealCrawlersList({
    vendorId: defaultVendorId,
    enabled: open,
  });

  const dealCrawlers = useMemo(() => data?.result ?? [], [data]);

  const filteredRows = useMemo(() => {
    const nameLower = nameFilter.trim().toLowerCase();
    if (!nameLower) {
      return dealCrawlers;
    }
    return dealCrawlers.filter((row) => (row.dealPageName ?? '').toLowerCase().includes(nameLower));
  }, [dealCrawlers, nameFilter]);

  const displayRows = useMemo(
    () => filteredRows.filter((row) => (row.sailingsByStatus?.COMPLETED ?? 0) > 0),
    [filteredRows],
  );

  // Pre-select crawlers and main when the list loads
  useEffect(() => {
    if (open && dealCrawlers.length > 0) {
      const idsInList = new Set(dealCrawlers.map((row) => row.id));
      const initialSelection = selectedCrawlerIds.filter((id) => idsInList.has(id));
      setRowSelectionModel(initialSelection);
      const firstSelected = initialSelection[0];
      const nextMain =
        (mainCrawlerIdProp && idsInList.has(mainCrawlerIdProp) ? mainCrawlerIdProp : null) ??
        (firstSelected && idsInList.has(firstSelected) ? firstSelected : null);
      setMainCrawlerId(nextMain);
    }
  }, [open, dealCrawlers, selectedCrawlerIds, mainCrawlerIdProp]);

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setNameFilter(e.target.value ?? '');
  }, []);

  const handleRowSelectionChange = useCallback(
    (model: GridRowSelectionModel) => {
      const next = Array.isArray(model) ? model : [];
      if (next.length > MAX_SELECTION) {
        enqueueSnackbar(`You can select at most ${MAX_SELECTION} crawled pages.`, { variant: 'warning' });
        return;
      }
      setRowSelectionModel(next);
      const selectedSet = new Set(next.map(String));
      setMainCrawlerId((prev) => {
        if (prev && selectedSet.has(prev)) {
          return prev;
        }
        return next.length ? String(next[0]) : null;
      });
    },
    [enqueueSnackbar],
  );

  const handleConfirm = useCallback(() => {
    const selectedIds = Array.isArray(rowSelectionModel) ? rowSelectionModel : [];
    if (selectedIds.length > MAX_SELECTION) {
      enqueueSnackbar(`You can select at most ${MAX_SELECTION} crawled pages.`, { variant: 'error' });
      return;
    }
    const selected = displayRows.filter((row) => selectedIds.includes(row.id));
    const vendorIds = new Set(selected.map((row) => row.vendorId));
    if (vendorIds.size > 1) {
      enqueueSnackbar('All selected crawlers must be from the same cruise line.', { variant: 'error' });
      return;
    }
    const selectedIdSet = new Set(selected.map((row) => row.id));
    const effectiveMain = mainCrawlerId && selectedIdSet.has(mainCrawlerId) ? mainCrawlerId : selected[0]?.id ?? '';
    onConfirm(selected, effectiveMain);
    setRowSelectionModel([]);
    setMainCrawlerId(null);
    setNameFilter('');
    onClose();
  }, [rowSelectionModel, displayRows, mainCrawlerId, onConfirm, onClose, enqueueSnackbar]);

  const handleClose = useCallback(() => {
    setRowSelectionModel([]);
    setMainCrawlerId(null);
    setNameFilter('');
    onClose();
  }, [onClose]);

  const handleSetMain = useCallback((crawlerId: string) => {
    setMainCrawlerId(crawlerId);
  }, []);

  const columns: Array<GridColDef<CruiseDealCrawler>> = useMemo(
    () => [
      {
        field: 'dealPageName',
        headerName: 'Name',
        flex: 2,
        minWidth: 180,
        sortable: false,
        disableColumnMenu: true,
      },
      {
        field: 'sourceUrl',
        headerName: 'Source URL',
        flex: 2,
        minWidth: 200,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => {
          const url = row.sourceUrl;
          const label = url.replace(/\/+$/, '').split('/').pop() ?? url;
          return (
            <a href={url} target="_blank" rel="noopener noreferrer">
              {label}
            </a>
          );
        },
      },
      {
        field: 'sailingsByStatus',
        headerName: 'Status',
        flex: 1,
        minWidth: 160,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => {
          const s = row.sailingsByStatus ?? {};
          const pending = s.PENDING ?? 0;
          const completed = s.COMPLETED ?? 0;
          const failed = s.FAILED ?? 0;
          return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {completed > 0 && <Chip icon={<CheckCircleIcon />} label={completed} color="success" size="small" />}
              {pending > 0 && <Chip icon={<ScheduleIcon />} label={pending} color="warning" size="small" />}
              {failed > 0 && <Chip icon={<ErrorIcon />} label={failed} color="error" size="small" />}
            </Stack>
          );
        },
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        flex: 1,
        minWidth: 100,
        sortable: false,
        disableColumnMenu: true,
        valueGetter: (_, row) => row.createdAt,
        renderCell: ({ value }) => (value ? formatDateShort(value) : '—'),
      },
      {
        field: 'main',
        headerName: 'Main',
        flex: 1,
        minWidth: 100,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => {
          const selectedIds = new Set((Array.isArray(rowSelectionModel) ? rowSelectionModel : []).map(String));
          const isSelected = selectedIds.has(row.id);
          if (row.id === mainCrawlerId) {
            return (
              <Typography variant="caption" color="primary" fontWeight="bold">
                Main
              </Typography>
            );
          }
          return (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              disabled={!isSelected}
              onClick={(e) => {
                e.stopPropagation();
                handleSetMain(row.id);
              }}
            >
              Set as main
            </Button>
          );
        },
      },
    ],
    [mainCrawlerId, handleSetMain, rowSelectionModel],
  );

  const selectedCount = Array.isArray(rowSelectionModel) ? rowSelectionModel.length : 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Select crawled pages</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <TextField
              label="Filter by deal page name"
              value={nameFilter}
              onChange={handleNameChange}
              placeholder="Filter by deal page name"
              size="small"
              sx={{ minWidth: 400 }}
            />
          </Stack>

          {!isLoading && displayRows.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3 }} textAlign="center">
              There are no crawlers with completed sailings for this cruise line.
            </Typography>
          ) : (
            <DataGrid
              loading={isLoading}
              rows={displayRows}
              columns={columns}
              getRowId={(row) => row.id}
              checkboxSelection
              rowSelectionModel={rowSelectionModel}
              onRowSelectionModelChange={handleRowSelectionChange}
              pagination
              paginationMode="client"
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              autoHeight
              disableRowSelectionOnClick={false}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" color="primary" onClick={handleConfirm} disabled={selectedCount === 0}>
          {selectedCount > 0 ? `Add ${selectedCount} selected` : 'Add selected'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
